import { useState } from 'react';
import { Copy, Eye, EyeOff, GraduationCap } from 'lucide-react';
import type { Admission } from '@/api/applicant';
import { naira } from '@/lib/format';
import { useAuth } from '@/stores/auth';

/** Shown once the school admits the applicant: their class, reg number and the password for the school app. */
export function AdmissionCard({ admission, name }: { admission: Admission; name?: string }) {
  const [show, setShow] = useState(false);
  const copy = (v: string) => navigator.clipboard?.writeText(v);
  const logout = useAuth((s) => s.logout);
  // The student account is separate from this applicant account, so sign out first.
  const toStudentLogin = () => { logout(); window.location.assign('/login'); };
  return (
    <div className="rounded-2xl overflow-hidden border border-green-200 shadow-sm">
      <div className="bg-[#0A2540] text-white px-5 py-4 flex items-center gap-3">
        <GraduationCap className="w-7 h-7 text-green-400 shrink-0" />
        <div><p className="text-lg font-extrabold">Congratulations{name ? `, ${name.split(' ')[0]}` : ''}!</p><p className="text-sm text-slate-300">You are admitted into {admission.class?.name ?? 'the school'}.</p></div>
      </div>
      <div className="bg-white p-5 space-y-3">
        <dl className="divide-y divide-slate-100 text-sm">
          <div className="flex items-center justify-between py-2"><dt className="text-slate-500">Reg number</dt>
            <dd className="font-mono font-bold text-slate-900 flex items-center gap-2">{admission.admission_no}<button onClick={() => copy(admission.admission_no)} aria-label="Copy reg number"><Copy className="w-3.5 h-3.5 text-slate-400" /></button></dd></div>
          {admission.password && (
            <div className="flex items-center justify-between py-2"><dt className="text-slate-500">Password</dt>
              <dd className="font-mono font-bold text-slate-900 flex items-center gap-2">{show ? admission.password : '••••••••'}
                <button onClick={() => setShow(!show)} aria-label={show ? 'Hide password' : 'Show password'}>{show ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}</button>
                <button onClick={() => copy(admission.password!)} aria-label="Copy password"><Copy className="w-3.5 h-3.5 text-slate-400" /></button></dd></div>
          )}
          <div className="flex items-center justify-between py-2"><dt className="text-slate-500">Class</dt><dd className="font-semibold">{admission.class?.name ?? '—'}</dd></div>
          {admission.acceptance_fee ? <div className="flex items-center justify-between py-2"><dt className="text-slate-500">Acceptance fee</dt><dd className="font-semibold">{naira(admission.acceptance_fee)}</dd></div> : null}
        </dl>
        <p className="text-xs text-slate-600 bg-green-50 rounded-lg px-3 py-2">{admission.login_hint}</p>
        <button onClick={toStudentLogin} className="block w-full text-center bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl py-3 text-sm">Sign out and sign in as a student</button>
      </div>
    </div>
  );
}
